/**
 * useAuthIntegrityCheck Hook
 * 
 * Global safety mechanism that runs immediately when the app starts.
 * Prevents incorrect session mixing between companies by detecting invalid or mismatched authentication state.
 * 
 * If ANY inconsistency is detected, the system automatically performs a full "forceLogout":
 * - Clear React Query cache
 * - Clear all CRM-related localStorage keys
 * - Clear axios default headers
 * - Reset all auth/company context
 * - Redirect user immediately to /login
 * 
 * This check runs BEFORE any sidebar prefetch or bootstrap to prevent misrouting.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthMe } from '../api/settingsApi';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from '../utils/companySlug';
import { createForceLogout } from '../utils/forceLogout';
import { isSuperadminPath, isCompanyRoute, isSuperadminRole } from '../utils/routeHelpers';
import { perfLog } from '../utils/performance';

/**
 * Get impersonation info from cookies
 */
function getImpersonationInfo(): { isImpersonating: boolean; impersonationCompany: string | null } {
  if (typeof document === 'undefined') {
    return { isImpersonating: false, impersonationCompany: null };
  }
  
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {} as Record<string, string>);
  
  const impersonationCompany = cookies['impersonation_company'] || null;
  const impersonationToken = cookies['impersonation_token'] || null;
  
  return {
    isImpersonating: !!(impersonationCompany && impersonationToken),
    impersonationCompany,
  };
}

/**
 * Hook to check auth integrity on app startup
 * 
 * Checks for:
 * 1. Token exists but company missing
 * 2. Company exists but token missing
 * 3. Mismatch between server-side session company and stored company
 * 
 * If any mismatch is detected, forces immediate logout.
 */
export function useAuthIntegrityCheck() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const hasRun = useRef(false);
  const isChecking = useRef(false);
  
  // Create forceLogout function using centralized helper (memoized to avoid recreation)
  // Runtime safety guard: try-catch prevents ReferenceError if createForceLogout is undefined
  const forceLogout = useCallback((reason: string, companySlug?: string) => {
    try {
      // Create the logout function using the centralized helper
      const logoutFn = createForceLogout(queryClient);
      // Use options object format for better reason tracking
      logoutFn({ reason: reason as any, source: 'auth_integrity', companySlug });
    } catch (err) {
      // Safety fallback: if createForceLogout is undefined or throws, do basic cleanup
      console.error('[AuthIntegrity] Error in forceLogout (using fallback):', err);
      try {
        // Basic cleanup fallback
        queryClient.clear();
        localStorage.removeItem('token');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('client');
        sessionStorage.clear();
        if (typeof window !== 'undefined') {
          const slugFromUrl = getCompanySlugFromLocation();
          const env = getRuntimeEnvFromHostname();
          const company = companySlug || slugFromUrl || resolveActiveCompanySlug({ env }) || '';
          if (company) {
            window.location.href = `/${company}/login`;
          } else {
            window.location.href = '/';
          }
        }
      } catch (fallbackErr) {
        console.error('[AuthIntegrity] Fallback cleanup also failed:', fallbackErr);
        // Last resort: just try to redirect
        if (typeof window !== 'undefined') {
          const slugFromUrl = getCompanySlugFromLocation();
          const env = getRuntimeEnvFromHostname();
          const company = companySlug || slugFromUrl || resolveActiveCompanySlug({ env }) || '';
          if (company) {
            window.location.href = `/${company}/login`;
          } else {
            window.location.href = '/';
          }
        }
      }
    }
  }, [queryClient]);
  
  useEffect(() => {
    // Prevent running twice in StrictMode
    if (hasRun.current || isChecking.current) return;
    
    // Skip check on login/register pages
    if (location.pathname.includes('/login') || location.pathname.includes('/register')) {
      return;
    }
    
    isChecking.current = true;
    hasRun.current = true;
    
    const pathname = location.pathname;
    const superadminRoute = isSuperadminPath(pathname);
    const companyRoute = isCompanyRoute(pathname);
    const { isImpersonating } = getImpersonationInfo();
    
    // Get user role from token
    let userRole: string | null = null;
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        userRole = payload?.role || null;
      } catch (e) {
        // Invalid token format
      }
    }
    
    const isSuperadmin = isSuperadminRole(userRole);
    
    // CRITICAL: For superadmin routes, skip company-based integrity checks
    // Superadmin doesn't need company context
    if (superadminRoute && isSuperadmin) {
      perfLog.debug('[IntegrityCheck] Superadmin context - skipping company checks');
      isChecking.current = false;
      return;
    }
    
    // CRITICAL: For company routes with superadmin role, check impersonation
    // If superadmin is on company route without impersonation, this is handled by ProtectedRoute
    // We just skip the integrity check here (ProtectedRoute will redirect)
    if (companyRoute && isSuperadmin && !isImpersonating) {
      perfLog.debug('[IntegrityCheck] Superadmin on company route without impersonation - skipping check');
      isChecking.current = false;
      return;
    }
    
    // Use new localStorage key, fallback to old key for backward compatibility
    const storedCompany = localStorage.getItem('wjs_active_company_v2') || localStorage.getItem('currentCompany');
    
    // Convert null to undefined for type safety
    const companySlug = storedCompany || undefined;
    
    // 🔥 DISABLED: Auto logout on integrity mismatch - completely disabled
    // CASE 1: Token exists but no company stored (only for company routes)
    // DO NOT logout - just log the issue and continue
    if (token && !companySlug && companyRoute) {
      perfLog.debug('[IntegrityCheck] Token exists but company missing - NOT logging out (auto logout disabled)');
      // Try to get company from URL
      const urlCompany = getCompanySlugFromLocation();
      if (urlCompany && urlCompany !== 'superadmin') {
        try {
          localStorage.setItem('wjs_active_company_v2', urlCompany);
        } catch (e) {
          perfLog.warn('[IntegrityCheck] Failed to update localStorage:', e);
        }
      }
      isChecking.current = false;
      return;
    }
    
    // 🔥 DISABLED: Auto logout on missing token - completely disabled
    // CASE 2: Company stored but no token (only for company routes)
    // DO NOT logout - just log the issue and continue
    if (!token && companySlug && companyRoute) {
      perfLog.debug('[IntegrityCheck] Company stored but token missing - NOT logging out (auto logout disabled)');
      isChecking.current = false;
      return;
    }
    
    // CASE 3: Both exist - validate with server (only for company routes)
    if (token && companySlug && companyRoute) {
      // TypeScript guard: companySlug is definitely string here
      const company = companySlug;
      
      // Get company from URL to validate against (URL is always source of truth)
      const urlCompany = getCompanySlugFromLocation();
      
      // If URL company exists and doesn't match stored company, URL takes precedence
      // This is expected behavior - URL is always source of truth
      if (urlCompany && urlCompany !== company && urlCompany !== 'superadmin') {
        perfLog.debug('[IntegrityCheck] URL company differs from stored - URL takes precedence');
        // Don't force logout - URL is the source of truth, just update localStorage
        try {
          localStorage.setItem('wjs_active_company_v2', urlCompany);
        } catch (e) {
          perfLog.warn('[IntegrityCheck] Failed to update localStorage:', e);
        }
        // Continue with validation using URL company
      }
      
      // Validate session with server
      async function validateSession() {
        try {
          // CRITICAL: For superadmin routes, skip company-based validation
          // Superadmin doesn't need company context
          if (superadminRoute && isSuperadmin) {
            perfLog.debug('[IntegrityCheck] Superadmin route - skipping company-based auth validation');
            isChecking.current = false;
            return;
          }
          
          // Use stored company for validation (company is definitely string here)
          const me = await getAuthMe(company);
          
          // Check if response has company info
          const backendCompany = me?.company?.slug || me?.company || company;
          
          // 🔥 DISABLED: Auto logout on company mismatch - completely disabled
          // If backend company doesn't match stored company, just update localStorage
          if (backendCompany && backendCompany !== company && backendCompany !== 'superadmin') {
            perfLog.debug('[IntegrityCheck] Server company mismatch - updating localStorage (NOT logging out)');
            try {
              localStorage.setItem('wjs_active_company_v2', backendCompany);
            } catch (e) {
              perfLog.warn('[IntegrityCheck] Failed to update localStorage:', e);
            }
            // Continue - don't logout
          }
          
          // 🔥 CRITICAL: Check if user is BLOCKED - this is the ONLY valid auto-logout scenario
          if (me?.isBlocked === true || me?.client?.isBlocked === true) {
            perfLog.warn('[IntegrityCheck] User is BLOCKED - triggering blocked user flow');
            // Emit blocked event to trigger BlockedUserSocketModal
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('user:blocked', { 
                detail: {
                  message: 'Your access has been blocked by the administrator.',
                  isBlocked: true,
                  userId: null,
                  source: 'useAuthIntegrityCheck'
                }
              }));
            }
            // DO NOT logout here - let BlockedUserSocketModal handle it with countdown
            isChecking.current = false;
            return;
          }
          
          // Session is valid
          perfLog.debug('[IntegrityCheck] Session validated successfully');
        } catch (err: any) {
          // If auth/me fails, it could mean:
          // 1. Token is invalid/expired
          // 2. User doesn't have access to this company
          // 3. Network error
          // 4. Single-session conflict (409)
          // 5. Server error (500) - DO NOT LOGOUT ON THIS
          
          const status = err?.response?.status;
          const errorCode = err?.response?.data?.code || err?.response?.data?.errorCode;
          const errorMessage = err?.response?.data?.error || err?.response?.data?.message || '';
          
          // 🔥 CRITICAL: NEVER logout on 500 errors - these are server errors, not auth errors
          if (status === 500) {
            perfLog.debug('[IntegrityCheck] /auth/me failed with 500 (server error) - NOT logging out');
            isChecking.current = false;
            return;
          }
          
          // Check for real single-session conflict (409 or SESSION_REVOKED)
          const isSingleSessionConflict = status === 409 || 
                                         errorCode === 'SESSION_REVOKED' || 
                                         errorMessage === 'SESSION_REVOKED' ||
                                         (typeof errorMessage === 'string' && errorMessage.includes('SESSION_REVOKED'));
          
          // Only force logout on REAL auth errors (401, 403, 409 with specific error codes), not network errors or 500s
          // NOTE: OTHER_SESSION_ACTIVE is NOT treated as an auth error - user should stay logged in
          const isAuthError = (status === 401 || status === 403 || status === 409) &&
                             (errorCode === 'INVALID_TOKEN' ||
                              errorCode === 'SESSION_EXPIRED' ||
                              errorCode === 'SESSION_REVOKED' ||
                              // REMOVED: errorCode === 'OTHER_SESSION_ACTIVE' - conflict sessions should NOT trigger logout
                              errorMessage.includes('Invalid token') ||
                              errorMessage.includes('Token expired') ||
                              errorMessage.includes('Session expired') ||
                              errorMessage.includes('Unauthorized'));
          
          const isNetworkError = err?.code === 'ECONNABORTED' || err?.code === 'ERR_NETWORK' || err?.isNetworkError;
          
          // 🔥 DISABLED: Auto logout on auth errors - completely disabled
          // All auth errors are handled WITHOUT logging out
          if (isAuthError) {
            if (isSingleSessionConflict) {
              // Real single-session conflict - another device is logged in
              // DO NOT logout - just log the issue
              perfLog.debug('[IntegrityCheck] Single-session conflict detected - NOT logging out (auto logout disabled)');
            } else {
              // Token invalid or expired
              // DO NOT logout - just log the issue
              perfLog.debug('[IntegrityCheck] /auth/me failed with auth error - NOT logging out (auto logout disabled)');
            }
          } else if (!isNetworkError) {
            // Non-network, non-auth errors (including 500s which we already handled above) - log but don't force logout
            perfLog.debug('[IntegrityCheck] /auth/me failed (non-auth error, NOT logging out)');
          }
          // Network errors are ignored - user might be offline
        } finally {
          isChecking.current = false;
        }
      }
      
      validateSession();
    } else {
      // No token and no company - user is not logged in, which is fine
      isChecking.current = false;
    }
  }, [location.pathname, forceLogout]); // Re-run if pathname changes (e.g., navigation)
}

